/**
 * Parse subtitle content (auto-detects SRT or ASS format)
 * @param {string} content - Raw subtitle content (SRT or ASS)
 * @returns {Array<{startTime: number, endTime: number, text: string}>}
 */
export function parseSubtitles(content) {
  if (!content) return [];

  // Auto-detect format: ASS files contain "Dialogue:" lines, SRT files contain "-->" timestamps
  if (content.includes('Dialogue:') && content.includes('[Events]')) {
    return parseASSToSubtitles(content);
  }
  return parseSRTToSubtitles(content);
}

/**
 * Parse SRT subtitle format to JSON array
 * @param {string} srtContent - Raw SRT subtitle content
 * @returns {Array<{startTime: number, endTime: number, text: string}>}
 */
export function parseSRTToSubtitles(srtContent) {
  if (!srtContent) return [];

  const subtitles = [];
  const blocks = srtContent.trim().split(/\n\n+/);

  for (const block of blocks) {
    const lines = block.trim().split('\n');
    if (lines.length < 3) continue;

    // Line 1: index, Line 2: timestamps, Line 3+: text
    const timeMatch = lines[1].match(/(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/);
    if (!timeMatch) continue;

    const startTime = parseSRTTime(timeMatch[1]);
    const endTime = parseSRTTime(timeMatch[2]);
    const text = lines.slice(2).join(' ').trim();

    if (text) {
      subtitles.push({ startTime, endTime, text });
    }
  }

  return subtitles;
}

/**
 * Parse SRT time format to seconds
 * @param {string} timeStr - Time in format HH:MM:SS,mmm
 * @returns {number} Time in seconds
 */
export function parseSRTTime(timeStr) {
  const match = timeStr.trim().match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
  if (!match) return 0;

  return parseInt(match[1]) * 3600 + parseInt(match[2]) * 60 + parseInt(match[3]) + parseInt(match[4]) / 1000;
}

/**
 * Parse ASS (Advanced SubStation Alpha) subtitle format to JSON array
 * @param {string} assContent - Raw ASS subtitle content
 * @returns {Array<{startTime: number, endTime: number, text: string}>}
 */
export function parseASSToSubtitles(assContent) {
  if (!assContent) return [];

  const lines = assContent.split('\n');
  const subtitles = [];

  for (const line of lines) {
    if (line.startsWith('Dialogue:')) {
      // Format: Dialogue: Layer,Start,End,Style,Name,MarginL,MarginR,MarginV,Effect,Text
      const parts = line.substring(10).split(',');

      if (parts.length >= 10) {
        const startTime = parseASSTime(parts[1]);
        const endTime = parseASSTime(parts[2]);
        const text = parts.slice(9).join(','); // Text may contain commas

        subtitles.push({
          startTime,
          endTime,
          text: text.trim()
        });
      }
    }
  }

  return subtitles;
}

/**
 * Parse ASS time format to seconds
 * @param {string} timeStr - Time in format H:MM:SS.CC (centiseconds)
 * @returns {number} Time in seconds
 */
export function parseASSTime(timeStr) {
  // Format: H:MM:SS.CC (centiseconds)
  const match = timeStr.trim().match(/(\d+):(\d{2}):(\d{2})\.(\d{2})/);
  if (!match) return 0;

  const hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const seconds = parseInt(match[3]);
  const centiseconds = parseInt(match[4]);

  return hours * 3600 + minutes * 60 + seconds + centiseconds / 100;
}

/**
 * Get the current subtitle based on playback time
 * @param {Array<{startTime: number, endTime: number, text: string}>} subtitles
 * @param {number} currentTime - Current playback time in seconds
 * @returns {{startTime: number, endTime: number, text: string} | null}
 */
export function getCurrentSubtitle(subtitles, currentTime) {
  return subtitles.find(sub =>
    currentTime >= sub.startTime && currentTime < sub.endTime
  ) || null;
}
